import {
  VALIDATE_DESIGN_ELEMENTS,
  VALIDATE_ELEMENT,
  DROP_ELEMENT,
  DROP_SELECTED_ELEMENTS,
  MOVE_ELEMENT,
  RESIZE_ELEMENT,
  MOVE_LEFT,
  MOVE_RIGHT,
  MOVE_UP,
  MOVE_DOWN
} from "../../actions/";
import ItemTypes from "../../constants/ItemTypes";

const initialState = {
  numInvalidElements: 0,
  largePlate: false
};

function checkElementBounds(element, layerWidth, layerHeight) {
  // Skip validation if element is not fully loaded or dimensions are not set
  if (
    !element ||
    typeof element.width === "undefined" ||
    typeof element.height === "undefined" ||
    typeof element.left_position === "undefined" ||
    typeof element.top_position === "undefined"
  ) {
    console.debug("Skipping validation - missing dimensions", element);
    return false;
  }

  // Convert dimensions to actual values with better precision handling
  // Use Math.round to handle floating point precision issues
  const precision = 1000; // 3 decimal places
  const elWidth = Math.round(element.width * precision) / precision;
  const elHeight = Math.round(element.height * precision) / precision;
  const elLeft = Math.round(element.left_position * precision) / precision;
  const elTop = Math.round(element.top_position * precision) / precision;

  // Skip validation if dimensions are 0 or negative
  if (elWidth <= 0 || elHeight <= 0) {
    console.debug("Skipping validation - invalid dimensions", {
      elWidth,
      elHeight
    });
    return false;
  }

  // Calculate total occupied space with better precision
  const totalWidth = Math.round((elLeft + elWidth) * precision) / precision;
  const totalHeight = Math.round((elTop + elHeight) * precision) / precision;

  // Increase tolerance for edge cases (0.005 inches)
  const TOLERANCE = 0.005;

  // Check if element is outside bounds with tolerance
  // For left and top edges, we use a slightly larger tolerance
  const isOutsideBounds =
    totalWidth > layerWidth + TOLERANCE || // Right edge beyond bounds
    totalHeight > layerHeight + TOLERANCE || // Bottom edge beyond bounds
    elLeft < -(TOLERANCE * 2) || // Left edge beyond bounds (more forgiving)
    elTop < -(TOLERANCE * 2); // Top edge beyond bounds (more forgiving)

  if (isOutsideBounds) {
    console.debug("Element out of bounds", {
      element,
      bounds: { layerWidth, layerHeight },
      position: { left: elLeft, top: elTop },
      dimensions: { width: elWidth, height: elHeight },
      total: { width: totalWidth, height: totalHeight },
      comparisons: {
        rightEdge: totalWidth > layerWidth + TOLERANCE,
        bottomEdge: totalHeight > layerHeight + TOLERANCE,
        leftEdge: elLeft < -(TOLERANCE * 2),
        topEdge: elTop < -(TOLERANCE * 2)
      }
    });
  }

  return isOutsideBounds;
}

export default function (state = initialState, action) {
  const design = state;
  var largePlateFlag = isLargePlate(design);

  switch (action.type) {
    case VALIDATE_DESIGN_ELEMENTS:
    case DROP_ELEMENT:
    case DROP_SELECTED_ELEMENTS:
    case MOVE_ELEMENT:
    case MOVE_LEFT:
    case MOVE_RIGHT:
    case MOVE_UP:
    case MOVE_DOWN:
    case RESIZE_ELEMENT:
      let invalidElements = 0;

      // Check that all elements are within printable area
      state.layer_set.forEach((layer) => {
        if (layer.width > 0 && (layer.allow_image || layer.allow_text)) {
          const layerWidth = layer.width,
            layerHeight = layer.height;

          if (layer.text_elements) {
            layer.text_elements.forEach((element) => {
              if (checkElementBounds(element, layerWidth, layerHeight)) {
                invalidElements++;
              }
            });
          }

          if (layer.image_elements) {
            layer.image_elements.forEach((element) => {
              if (checkElementBounds(element, layerWidth, layerHeight)) {
                invalidElements++;
              }
            });
          }
        }
      });

      return {
        ...state,
        numInvalidElements: invalidElements,
        largePlate: largePlateFlag
      };

    case VALIDATE_ELEMENT:
      let invalidElement = 0;
      const layer = state.layer_set.find(
        (obj) => obj.layer_id === action.payload.layerId
      );

      // Return current state if layer not found
      if (!layer) {
        return {
          ...state,
          numInvalidElements: invalidElement,
          largePlate: largePlateFlag
        };
      }

      const layerWidth = layer.width,
        layerHeight = layer.height;

      let element = null;

      if (action.payload.itemType === ItemTypes.TEXT) {
        element =
          layer.text_elements && layer.text_elements[action.payload.index];
      } else {
        element =
          layer.image_elements && layer.image_elements[action.payload.index];
      }

      // Return current state if element not found
      if (!element) {
        return {
          ...state,
          numInvalidElements: invalidElement,
          largePlate: largePlateFlag
        };
      }

      if (checkElementBounds(element, layerWidth, layerHeight)) {
        invalidElement++;
      }

      return {
        ...state,
        numInvalidElements: invalidElement,
        largePlate: largePlateFlag
      };

    default:
      return state;
  }

  function isLargePlate(design) {
    if (design.disable_print_plate_check === true) {
      return false;
    }

    // Paper Cups = 116
    // Matches = 121
    // Frost Flex Cups = 897
    // Styrofoam Cups = 898
    // Can Coolers = 899
    // Stadium Cups = 900
    // Plastic Shot Glass = 1403
    const NO_LARGE_PRODUCT_TYPES = [116, 121, 897, 898, 899, 900, 1403];
    /*
    286	Cocktail Napkins
    399	Photo/Full Color Cocktail Napkins
    437	Shimmer Cocktail Napkins
    440	Patterned Cocktail Napkins
    446	Linen Like Cocktail Napkins
    509 Photo/Full Color Square Coasters
    520	Door Hanger
    521	Cocktail Napkins with Bleed
    556 Linen Like Petite Napkins
    583	Cocktail Napkins - 2 color
    596 Photo/Full Color Round Coasters
    704	4-ply Cocktail Napkins
    711	Foil Embossed Cocktail Napkins
    712	Woven Cocktail Napkins
    717	Ink Cocktail Napkins
    856 Ink Printed Luncheon Napkins
    857 Ink Printed Guest Towels
     */
    const NO_LARGE_PRODUCT_IDS = [
      286, 399, 437, 440, 446, 509, 520, 521, 556, 583, 596, 704, 711, 712, 717,
      719, 724, 856, 857
    ];

    if (
      NO_LARGE_PRODUCT_TYPES.includes(Number(design.product_type_id)) ||
      NO_LARGE_PRODUCT_IDS.includes(Number(design.product_variant_id))
    ) {
      return false; // don't check plate size
    }

    const layers = design.layer_set;

    // image Layers to check contents
    const imageLayers = layers.filter((layer) => {
      return layer.allow_image;
    });

    // only check if not photo product or using custom art
    imageLayers.forEach((layer) => {
      if (layer.image_elements.length) {
        layer.image_elements.forEach((element) => {
          if (element.type === "photo" || element.type === "art") {
            return false; // don't check plate size
          }
        });
      }
    });

    // Layers to check the combined dimensions of elements
    const editableLayers = layers.filter((layer) => {
      return layer.allow_text || layer.allow_image;
    });

    let rightMostTextPosition = 0,
      leftMostTextPosition = Number.POSITIVE_INFINITY,
      bottomMostTextPosition = 0,
      topMostTextPosition = Number.POSITIVE_INFINITY,
      rightMostImagePosition = 0,
      leftMostImagePosition = Number.POSITIVE_INFINITY,
      bottomMostImagePosition = 0,
      topMostImagePosition = Number.POSITIVE_INFINITY;

    function ignoreableLargeClipart(element) {
      // check if large clipart and doesn't overlap
      if (element.type === "clipart" && element.size === "large") {
        //TODO: RWH - commented out to skip this calculation until such time as we decide wreaths and frames are not important
        //let element_bottom_position = element.height + element.top_position,
        //  element_right_position = element.width + element.left_position
        //if((element.top_position >= topMostTextPosition && element.top_position <= bottomMostTextPosition &&           // top
        //  element.left_position >= leftMostTextPosition && element.left_position <= rightMostTextPosition) ||        // left image pixel is within the text bounds
        //  (element_bottom_position >= topMostTextPosition && element_bottom_position <= bottomMostTextPosition &&     // or bottom
        //  element_right_position >= leftMostTextPosition && element_right_position <= rightMostTextPosition) ||      // right image pixel is within the text bounds
        //  (topMostTextPosition >= element.top_position && topMostTextPosition <= element_bottom_position &&           // top
        //  leftMostTextPosition >= element.left_position && leftMostTextPosition <= element_right_position) ||        // left text pixel is within the image bounds
        //  (bottomMostTextPosition >= element.top_position && bottomMostTextPosition <= element_bottom_position &&     // or bottom
        //  rightMostTextPosition >= element.left_position && bottomMostTextPosition <= element_right_position)){     // right text pixel is within the image bounds
        //  return false
        //} else {
        return true;
        //}
      }
      return false;
    }

    /*
     * TEXT CALCULATIONS
     */

    // check ALL layers for each text and image instead of filtering each beforehand
    // that way we should never have empty array returned
    // (unless there are no text or image layers but that shouldn't happen)

    // get left, right, top and bottom most position of all text elements
    rightMostTextPosition = Math.max.apply(
      null,
      editableLayers.map((layer) => {
        if (layer.text_elements.length) {
          return Math.max.apply(
            null,
            layer.text_elements.map((element) => {
              return element.width + element.left_position;
            })
          );
        }
        return 0;
      })
    );

    leftMostTextPosition = Math.min.apply(
      null,
      editableLayers.map((layer) => {
        if (layer.text_elements.length) {
          return Math.min.apply(
            null,
            layer.text_elements.map((element) => {
              return element.left_position;
            })
          );
        }
        return Number.POSITIVE_INFINITY;
      })
    );

    bottomMostTextPosition = Math.max.apply(
      null,
      editableLayers.map((layer) => {
        if (layer.text_elements.length) {
          return Math.max.apply(
            null,
            layer.text_elements.map((element) => {
              return element.height + element.top_position;
            })
          );
        }
        return 0;
      })
    );

    topMostTextPosition = Math.min.apply(
      null,
      editableLayers.map((layer) => {
        if (layer.text_elements.length) {
          return Math.min.apply(
            null,
            layer.text_elements.map((element) => {
              return element.top_position;
            })
          );
        }
        return Number.POSITIVE_INFINITY;
      })
    );

    /*
     * IMAGE CALCULATIONS
     */

    // get left, right, top and bottom most position of all image elements
    rightMostImagePosition = Math.max.apply(
      null,
      editableLayers.map((layer) => {
        if (layer.image_elements.length) {
          // && !(layer.image_elements.type === 'clipart' && layer.image_elements.size === 'large')) {
          return Math.max.apply(
            null,
            layer.image_elements.map((element) => {
              if (ignoreableLargeClipart(element)) {
                return 0;
              }
              return element.width + element.left_position;
            })
          );
        }
        return 0;
      })
    );

    leftMostImagePosition = Math.min.apply(
      null,
      editableLayers.map((layer) => {
        if (layer.image_elements.length) {
          return Math.min.apply(
            null,
            layer.image_elements.map((element) => {
              if (ignoreableLargeClipart(element)) {
                return Number.POSITIVE_INFINITY;
              }
              return element.left_position;
            })
          );
        }
        return Number.POSITIVE_INFINITY;
      })
    );

    bottomMostImagePosition = Math.max.apply(
      null,
      editableLayers.map((layer) => {
        if (layer.image_elements.length) {
          return Math.max.apply(
            null,
            layer.image_elements.map((element) => {
              if (ignoreableLargeClipart(element)) {
                return 0;
              }
              return element.height + element.top_position;
            })
          );
        }
        return 0;
      })
    );

    topMostImagePosition = Math.min.apply(
      null,
      editableLayers.map((layer) => {
        if (layer.image_elements.length) {
          return Math.min.apply(
            null,
            layer.image_elements.map((element) => {
              if (ignoreableLargeClipart(element)) {
                return Number.POSITIVE_INFINITY;
              }
              return element.top_position;
            })
          );
        }
        return Number.POSITIVE_INFINITY;
      })
    );

    const rightMostPosition =
        rightMostTextPosition > rightMostImagePosition
          ? rightMostTextPosition
          : rightMostImagePosition,
      leftMostPosition =
        leftMostTextPosition < leftMostImagePosition
          ? leftMostTextPosition
          : leftMostImagePosition;

    let totalWidth = 0;
    if (rightMostPosition - leftMostPosition !== Number.NEGATIVE_INFINITY) {
      totalWidth = rightMostPosition - leftMostPosition;
    }

    const bottomMostPosition =
        bottomMostTextPosition > bottomMostImagePosition
          ? bottomMostTextPosition
          : bottomMostImagePosition,
      topMostPosition =
        topMostTextPosition < topMostImagePosition
          ? topMostTextPosition
          : topMostImagePosition;

    let totalHeight = 0;
    if (bottomMostPosition - topMostPosition !== Number.NEGATIVE_INFINITY) {
      totalHeight = bottomMostPosition - topMostPosition;
    }

    let totalArea = totalWidth * totalHeight;
    if (totalArea >= 9) {
      return true;
    }

    return false;
  }
}
